import { supabase } from '../lib/supabase'

export interface ProfileUpdateData {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  businessName?: string
  businessNames?: string[]
  bio?: string
  website?: string
  timezone?: string
  notificationPreferences?: {
    email: boolean
    sms: boolean
    push: boolean
    marketing: boolean
  }
  socialLinks?: {
    instagram?: string
    facebook?: string
    twitter?: string
    linkedin?: string
  }
}

export interface PasswordChangeData {
  currentPassword: string
  newPassword: string
}

class ProfileService {
  async updateProfile(userId: string, data: ProfileUpdateData) {
    try {
      const updateData: any = {}
      
      // Map the data to database column names
      if (data.firstName !== undefined) updateData.first_name = data.firstName
      if (data.lastName !== undefined) updateData.last_name = data.lastName
      if (data.email !== undefined) updateData.email = data.email
      if (data.phone !== undefined) updateData.phone = data.phone
      if (data.businessName !== undefined) updateData.business_name = data.businessName
      if (data.businessNames !== undefined) updateData.business_names = data.businessNames
      if (data.bio !== undefined) updateData.bio = data.bio
      if (data.website !== undefined) updateData.website = data.website
      if (data.timezone !== undefined) updateData.timezone = data.timezone
      if (data.notificationPreferences !== undefined) updateData.notification_preferences = data.notificationPreferences
      if (data.socialLinks !== undefined) updateData.social_links = data.socialLinks

      const { data: updatedProfile, error } = await supabase!
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single()

      if (error) {
        throw error
      }

      return { success: true, data: updatedProfile }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update profile' 
      }
    }
  }

  async uploadProfilePicture(userId: string, file: File) {
    try {
      console.log('Starting profile picture upload for user:', userId)
      console.log('File details:', { name: file.name, size: file.size, type: file.type })

      // Check if supabase is configured
      if (!supabase) {
        throw new Error('Supabase not configured')
      }

      // Create a unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${userId}/profile.${fileExt}`
      console.log('Upload filename:', fileName)

      // First, check if the bucket exists
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets()
      console.log('Available buckets:', buckets?.map(b => b.name))
      
      if (bucketError) {
        console.error('Error listing buckets:', bucketError)
      }

      // Try to create the bucket if it doesn't exist
      const bucketExists = buckets?.some(bucket => bucket.name === 'profile-pictures')
      if (!bucketExists) {
        console.log('Creating profile-pictures bucket...')
        const { error: createBucketError } = await supabase.storage.createBucket('profile-pictures', {
          public: true,
          allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
          fileSizeLimit: 5242880 // 5MB
        })
        
        if (createBucketError) {
          console.error('Error creating bucket:', createBucketError)
          // Continue anyway, bucket might already exist
        }
      }

      // Upload to Supabase Storage
      console.log('Uploading file to storage...')
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, {
          upsert: true // Replace existing file
        })

      console.log('Upload result:', { uploadData, uploadError })

      if (uploadError) {
        console.error('Upload error details:', uploadError)
        throw uploadError
      }

      // Get the public URL with cache busting
      console.log('Getting public URL...')
      const { data: urlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName)

      console.log('URL data:', urlData)

      // Add cache busting parameter to ensure immediate updates
      const profilePictureUrl = `${urlData.publicUrl}?t=${Date.now()}`
      console.log('Final profile picture URL:', profilePictureUrl)

      // Check if profile_picture column exists before updating
      console.log('Updating profile with new picture URL...')
      const { data: updateData, error: updateError } = await supabase
        .from('profiles')
        .update({ profile_picture: profilePictureUrl })
        .eq('id', userId)
        .select()

      console.log('Profile update result:', { updateData, updateError })

      if (updateError) {
        console.error('Profile update error details:', updateError)
        
        // If the column doesn't exist, provide a helpful error message
        if (updateError.message?.includes('column "profile_picture" does not exist')) {
          throw new Error('Database migration required: The profile_picture column does not exist. Please run the database migration script.')
        }
        
        throw updateError
      }

      console.log('Profile picture upload completed successfully')
      return { success: true, url: profilePictureUrl }
    } catch (error) {
      console.error('Error uploading profile picture:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to upload profile picture' 
      }
    }
  }

  async changePassword(data: PasswordChangeData) {
    try {
      // Supabase handles password changes through the auth API
      const { error } = await supabase!.auth.updateUser({
        password: data.newPassword
      })

      if (error) {
        throw error
      }

      return { success: true }
    } catch (error) {
      console.error('Error changing password:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to change password' 
      }
    }
  }

  async changeEmail(newEmail: string) {
    try {
      const { error } = await supabase!.auth.updateUser({
        email: newEmail
      })

      if (error) {
        throw error
      }

      return { success: true }
    } catch (error) {
      console.error('Error changing email:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to change email' 
      }
    }
  }

  async deleteProfilePicture(userId: string) {
    try {
      // List all files in the user's folder to find the profile picture
      const { data: files, error: listError } = await supabase!.storage
        .from('profile-pictures')
        .list(userId)

      if (listError) {
        console.warn('Could not list files for deletion:', listError)
      }

      // Remove all profile pictures for this user (handles different extensions)
      if (files && files.length > 0) {
        const filesToDelete = files
          .filter(file => file.name.startsWith('profile.'))
          .map(file => `${userId}/${file.name}`)
        
        if (filesToDelete.length > 0) {
          await supabase!.storage
            .from('profile-pictures')
            .remove(filesToDelete)
        }
      }

      // Update profile to remove picture URL
      const { error } = await supabase!
        .from('profiles')
        .update({ profile_picture: null })
        .eq('id', userId)

      if (error) {
        throw error
      }

      return { success: true }
    } catch (error) {
      console.error('Error deleting profile picture:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete profile picture' 
      }
    }
  }

  async getProfile(userId: string) {
    try {
      const { data, error } = await supabase!
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        throw error
      }

      return { success: true, data }
    } catch (error) {
      console.error('Error fetching profile:', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch profile' 
      }
    }
  }

  async checkMigrationStatus() {
    try {
      // Try to select the profile_picture column to see if it exists
      const { error } = await supabase!
        .from('profiles')
        .select('profile_picture')
        .limit(1)

      if (error && error.message?.includes('column "profile_picture" does not exist')) {
        return { migrationRequired: true, error: 'Database migration required' }
      }

      return { migrationRequired: false }
    } catch (error) {
      console.error('Error checking migration status:', error)
      return { migrationRequired: true, error: 'Could not check migration status' }
    }
  }
}

export const profileService = new ProfileService() 